import { FormEvent, useEffect, useState } from 'react';
import { api, Usuario } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Props {
  onClose: () => void;
}

interface PerfilForm {
  nome: string;
  email: string;
  empresa_nome: string;
  telefone: string;
  whatsapp: string;
  senha: string;
}

function valor(v?: string | null): string {
  return v || '';
}

export default function PerfilModal({ onClose }: Props) {
  const { user, atualizarPerfil } = useAuth();
  const [form, setForm] = useState<PerfilForm>({
    nome: user?.nome || '',
    email: user?.email || '',
    empresa_nome: valor(user?.empresa_nome),
    telefone: valor(user?.telefone),
    whatsapp: valor(user?.whatsapp),
    senha: '',
  });
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const podeEditarEmpresa = Boolean(user?.empresa_id && ['admin_empresa', 'supervisor', 'admin_geral'].includes(user?.perfil || ''));

  useEffect(() => {
    api<Usuario>('/usuarios/me.php')
      .then((dados) => {
        setForm({
          nome: dados.nome || '',
          email: dados.email || '',
          empresa_nome: valor(dados.empresa_nome),
          telefone: valor(dados.telefone),
          whatsapp: valor(dados.whatsapp),
          senha: '',
        });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : String(e)))
      .finally(() => setCarregando(false));
  }, []);

  function set<K extends keyof PerfilForm>(campo: K, valorCampo: PerfilForm[K]) {
    setForm((atual) => ({ ...atual, [campo]: valorCampo }));
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setSalvando(true);
    try {
      await atualizarPerfil({
        nome: form.nome.trim(),
        email: form.email.trim(),
        ...(podeEditarEmpresa ? { empresa_nome: form.empresa_nome.trim() } : {}),
        telefone: form.telefone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        ...(form.senha ? { senha: form.senha } : {}),
      });
      set('senha', '');
      setMsg('Dados atualizados.');
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="titulo-pg" style={{ marginBottom: 8 }}>
          <div>
            <h2>Meus dados</h2>
            {user?.perfil && <span className="badge">{user.perfil}</span>}
          </div>
          <button type="button" className="btn sec sm" onClick={onClose}>Fechar</button>
        </div>
        {erro && <div className="msg-erro">{erro}</div>}
        {msg && <div className="msg-ok">{msg}</div>}
        {carregando ? (
          <p className="vazio">Carregando dados...</p>
        ) : (
          <form onSubmit={salvar}>
            <div className="grid cols2">
              <div>
                <label>Nome *</label>
                <input className="input" value={form.nome} onChange={(e) => set('nome', e.target.value)} required />
              </div>
              <div>
                <label>E-mail *</label>
                <input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
              </div>
              {podeEditarEmpresa && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label>Nome da empresa *</label>
                  <input className="input" value={form.empresa_nome} onChange={(e) => set('empresa_nome', e.target.value)} required />
                </div>
              )}
              <div>
                <label>Telefone</label>
                <input className="input" value={form.telefone} onChange={(e) => set('telefone', e.target.value)} />
              </div>
              <div>
                <label>WhatsApp</label>
                <input className="input" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Nova senha</label>
                <input className="input" type="password" value={form.senha} onChange={(e) => set('senha', e.target.value)} minLength={6} placeholder="Preencha apenas se quiser trocar" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn sec" onClick={onClose}>Cancelar</button>
              <button className="btn" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar dados'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
